// Small dialog used after the user picks the target node in peer-connect
// mode. Captures the relationship label (e.g. "reads from", "authenticates
// via") before the peer edge is committed.

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";

const SUGGESTIONS = [
  "connects to",
  "reads from",
  "writes to",
  "authenticates via",
  "publishes to",
  "subscribes to",
  "depends on",
];

export default function PeerLabelDialog({
  open,
  onClose,
  sourceName,
  targetName,
  onSubmit,
}) {
  const [label, setLabel] = useState("connects to");

  useEffect(() => {
    if (open) setLabel("connects to");
  }, [open]);

  const submit = () => {
    const trimmed = label.trim() || "connects to";
    onSubmit?.(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="peer-label-dialog"
        className="max-w-[480px]"
        style={{
          background: "var(--color-background-primary)",
          border: "1px solid var(--color-border-primary)",
          color: "var(--color-text-primary)",
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ fontSize: 18, fontWeight: 600 }}>
            Describe the connection
          </DialogTitle>
          <DialogDescription style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
            <strong>{sourceName}</strong> → <strong>{targetName}</strong>
          </DialogDescription>
        </DialogHeader>

        <input
          value={label}
          autoFocus
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") onClose?.();
          }}
          data-testid="peer-label-input"
          maxLength={40}
          style={{
            background: "var(--color-background-secondary)",
            border: "1px solid var(--color-border-primary)",
            color: "var(--color-text-primary)",
            fontSize: 14,
            padding: "10px 12px",
            borderRadius: 8,
            outline: "none",
            width: "100%",
            marginTop: 4,
          }}
        />

        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setLabel(s)}
              data-testid={`peer-suggestion-${s.replace(/\s+/g, "-")}`}
              className="motion-micro"
              style={{
                background: "var(--color-background-tertiary)",
                border: "1px solid var(--color-border-primary)",
                color: "var(--color-text-secondary)",
                fontSize: 11,
                padding: "4px 10px",
                borderRadius: 9999,
                cursor: "pointer",
              }}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            data-testid="peer-label-cancel"
            className="motion-micro"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border-primary)",
              color: "var(--color-text-secondary)",
              fontSize: 13,
              padding: "8px 14px",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            data-testid="peer-label-submit"
            className="motion-micro"
            style={{
              background: "var(--color-accent)",
              color: "#0a0a0a",
              border: "none",
              fontSize: 13,
              fontWeight: 600,
              padding: "8px 16px",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            Add connection
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
